// The workspace landing page (consistency-pass spec §5): an index page with
// the Results time presets, the shared tiles and trend, and the latest
// responses. Per-survey detail lives on each survey's Results.

import { createFileRoute } from "@tanstack/react-router";
import { useOverview } from "@/api/overview";
import { IndexPage } from "@/components/index-page/index-page";
import { PageError, PageLoading, useRetry } from "@/components/page-states";
import { SectionCard } from "@/components/section-page/section-card";
import { bucketCaption } from "@/features/dashboard/stats-mapping";
import { ResponsesTrend } from "@/features/dashboard/trend-chart";
import { OverviewTiles } from "@/features/overview/overview-tiles";
import { isAllTime, overviewPreset, overviewWithin } from "@/features/overview/overview-window";
import { RecentFeed } from "@/features/overview/recent-feed";
import { useUrlSearch } from "@/features/search/page-search";
import { presetPatch } from "@/features/time/time-chip";
import { TimePresets } from "@/features/time/time-presets";

export const Route = createFileRoute("/w/$workspaceId/")({
    component: OverviewPage,
});

function OverviewPage() {
    const { workspaceId } = Route.useParams();
    const { rawSearch, onSearchPatch } = useUrlSearch();
    const allTime = isAllTime(rawSearch);
    const overviewQuery = useOverview(workspaceId, { within: overviewWithin(rawSearch) });
    const overview = overviewQuery.data;
    const overviewRetry = useRetry(overviewQuery);

    return (
        <IndexPage
            title="Overview"
            description="How guests are answering across every survey in this workspace."
            // The pressed preset is the one loaded (overviewWithin reads the same).
            action={
                <TimePresets
                    selected={overviewPreset(rawSearch)}
                    onPreset={(preset) => onSearchPatch(presetPatch(preset))}
                />
            }
        >
            {overviewRetry.failed ? (
                <PageError {...overviewRetry.props} />
            ) : !overview ? (
                // keepPreviousData holds the prior window across a change, so this is the first load only.
                <PageLoading />
            ) : (
                <>
                    <OverviewTiles overview={overview} allTime={allTime} />
                    <SectionCard id="overview-trend" title="Trend">
                        <p className="text-sm text-muted-foreground">{bucketCaption(overview.bucket)}</p>
                        <div className="mt-4 h-72">
                            <ResponsesTrend points={overview.trend} unit={overview.bucket} />
                        </div>
                    </SectionCard>
                    <RecentFeed workspaceId={workspaceId} recent={overview.recent} />
                </>
            )}
        </IndexPage>
    );
}
